import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { salidasApi } from '../../api/salidas'
import { fmtFecha, fmtFechaHora } from '../../utils/fecha'
import { copiarTexto } from '../../utils/copiar'
import { colors, font, text } from '../../design/tokens'
import { Card, Cargando, ErrorMsg, DocTag, ENTREGA_CFG } from './comunes'
import { resultadoSalida } from './MovilReparto'

// Detalle móvil de una salida: progreso + estado de cada parada (la paleta
// de resultados vive en comunes.ENTREGA_CFG, compartida con ViajesSeccion).

export default function MovilSalidaDetalle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [salida, setSalida] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    salidasApi.obtener(id)
      .then(({ data }) => setSalida(data))
      .catch(() => setError('No se pudo cargar la salida.'))
  }, [id])

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!salida) return <Cargando />

  const docs = salida.documentos || []
  const entregados = docs.filter((d) => d.estado_entrega === 'entregado').length
  const r = resultadoSalida({ ...salida, documentos_count: docs.length, entregados_count: entregados,
    no_entregados_count: docs.filter((d) => d.estado_entrega === 'no_entregado').length,
    reagendados_count: docs.filter((d) => d.estado_entrega === 'reagendado').length })

  return (
    <div>
      <div style={{ margin: '4px 0 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 17, fontWeight: 700, color: colors.text }}>
            Salida #{salida.id}
          </span>
          <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: r.bg, color: r.color }}>{r.label}</span>
        </div>
        <div style={{ fontFamily: font.sans, fontSize: 14, color: colors.textSec, marginTop: 3 }}>
          {salida.fletero?.interno ? '🏬 ' : '🚚 '}{salida.fletero?.nombre || 'Sin fletero'}
          <span style={{ color: colors.textMeta }}> · {fmtFecha(salida.fecha)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <div style={{ flex: 1, height: 7, background: colors.borderSoft, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: docs.length ? `${(entregados / docs.length) * 100}%` : '0%', height: '100%', background: colors.progreso, borderRadius: 4 }} />
          </div>
          <span style={{ fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' }}>
            {entregados}/{docs.length} entregados
          </span>
        </div>

        {/* La guía del fletero (portal público de la salida), como en la
            Distribución de escritorio: mandarla por WhatsApp o copiar el link.
            Solo viajes reales en la calle — un canal interno no tiene guía. */}
        {salida.token_acceso && !salida.fletero?.interno && salida.estado !== 'armando' && (
          <GuiaFletero salida={salida} />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {docs.map((doc) => {
          const esNE = doc.entidad_tipo === 'nota_envio'
          const ent = doc.entidad || {}
          const numero = esNE ? ent.numero_ne : ent.numero_presea
          const cliente = esNE ? ent.remito?.cliente : ent.cliente
          const dest = esNE ? ent.destinatario : null
          const zona = ent.zona?.nombre
          const cfg = ENTREGA_CFG[doc.estado_entrega]
          const to = esNE ? `/m/logistica/ne/${doc.entidad_id}` : `/m/logistica/remito/${doc.entidad_id}`

          // Dirección de ENTREGA — mismo criterio que el detalle de escritorio:
          // NE → destinatario; T90 → nv.direccion_envio > direccion_envio propia
          // (remito libre) > domicilio del cliente.
          const env = !esNE ? (ent.nota_venta?.direccion_envio || ent.direccion_envio) : null
          const tieneEnvio = env && (env.calle || env.localidad)
          const dirLinea = esNE
            ? [dest?.calle, dest?.localidad].filter(Boolean).join(', ')
            : (tieneEnvio ? [env.calle, env.piso, env.localidad].filter(Boolean).join(', ')
              : [cliente?.domicilio, cliente?.localidad].filter(Boolean).join(', '))
          const tel = esNE ? dest?.telefono : (tieneEnvio && env.telefono ? env.telefono : cliente?.telefono)

          return (
            <Card key={doc.id} onClick={() => navigate(to)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DocTag tipo={esNE ? 'ne' : 'remito'} numero={numero} />
                <span style={{ flex: 1 }} />
                {cfg ? (
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', background: cfg.bg, color: cfg.color }}>
                    {cfg.icono} {cfg.label}
                  </span>
                ) : (
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', background: colors.borderSoft, color: colors.textSec }}>
                    Pendiente
                  </span>
                )}
              </div>
              <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14, color: colors.text, margin: '5px 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(dest?.nombre || cliente?.nombre) || '—'}
              </div>
              {dirLinea && (
                <div style={{ fontSize: text.sec, color: colors.textSec, fontFamily: font.sans, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📍 {dirLinea}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 2 }}>
                {dest && cliente?.nombre && dest.nombre !== cliente.nombre && (
                  <span style={{ fontSize: text.sec, color: colors.textMeta, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente.nombre}</span>
                )}
                {tel && (
                  <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: text.sec, color: colors.link, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    📞 {tel}
                  </a>
                )}
                <span style={{ flex: 1 }} />
                {zona && <span style={zonaPill}>{zona}</span>}
              </div>
              {doc.estado_entrega && doc.entregado_at && (
                <div style={{ fontSize: 11.5, color: colors.textMeta, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  {fmtFechaHora(doc.entregado_at)}
                  {doc.receptor_nombre && <span style={{ fontFamily: font.sans }}> · recibió {doc.receptor_nombre}</span>}
                </div>
              )}
              {doc.motivo_no_entrega && (
                <div style={{ fontSize: 11.5, color: colors.err, marginTop: 3, fontFamily: font.sans }}>
                  Motivo: {doc.motivo_no_entrega}
                  {doc.nueva_fecha && <span style={{ color: colors.textSec }}> · nueva fecha {fmtFecha(doc.nueva_fecha)}</span>}
                </div>
              )}
            </Card>
          )
        })}
        {docs.length === 0 && (
          <p style={{ textAlign: 'center', color: colors.textMeta, fontSize: 13, padding: 20 }}>Sin documentos.</p>
        )}
      </div>
    </div>
  )
}

/**
 * Acciones de la hoja de ruta del fletero — el mismo criterio que los botones
 * de /logistica/distribucion (escritorio): WhatsApp con el link + copiar.
 * En el celular el WhatsApp abre la app directo, que es el caso real de uso
 * (el encargado despachando desde el teléfono).
 */
function GuiaFletero({ salida }) {
  const [copiado, setCopiado] = useState(false)
  const link = `${window.location.origin}/fletero/salida/${salida.token_acceso}`

  const whatsapp = () => {
    const tel = (salida.fletero?.telefono || '').replace(/\D/g, '')
    const destino = tel ? (tel.startsWith('54') ? tel : '54' + tel) : ''
    const msg = `Hola ${salida.fletero?.nombre || ''}, esta es tu hoja de ruta de hoy:\n${link}`
    window.open(destino
      ? `https://wa.me/${destino}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
  }

  const copiar = async () => {
    if (await copiarTexto(link)) { setCopiado(true); setTimeout(() => setCopiado(false), 1800) }
  }

  const btn = {
    flex: 1, padding: '9px 10px', borderRadius: 8, border: `1px solid ${colors.borderSoft}`,
    background: colors.bg, fontFamily: font.sans, fontSize: 13, fontWeight: 600,
    color: colors.text, cursor: 'pointer', textAlign: 'center', textDecoration: 'none',
  }

  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <button style={{ ...btn, background: '#eafaf1', borderColor: '#bbe6cd', color: '#1a7f4b' }} onClick={whatsapp}>
        📱 Enviar guía
      </button>
      <button style={btn} onClick={copiar}>{copiado ? '✓ Copiado' : '🔗 Copiar link'}</button>
      <a style={{ ...btn, flex: '0 0 auto' }} href={link} target="_blank" rel="noreferrer">↗</a>
    </div>
  )
}

const zonaPill = {
  fontSize: 11, color: colors.zonaText, background: colors.zonaBg, padding: '1px 7px',
  borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
}
